import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "../app/store";
import { initialLoyaltyCartState } from "../types/LoyaltyCartTypes";

export const selectLoyaltyCart = (state: RootState) =>
  state.cartLoyaltyV2Reducer.loyalty ?? initialLoyaltyCartState;

export const selectLoyaltyCartLines = (state: RootState) =>
  selectLoyaltyCart(state).lines;

export const selectLoyaltyCartResult = (state: RootState) =>
  selectLoyaltyCart(state).result;

export const selectLoyaltyRedemption = (state: RootState) =>
  selectLoyaltyCart(state).redemption;

export const selectLoyaltyApplyFlow = (state: RootState) =>
  selectLoyaltyCart(state).applyFlow;

export const selectCustomerLoyaltyOffersRaw = (state: RootState) =>
  selectLoyaltyCart(state).customerOffers;

export const selectRedeemedLoyaltyOfferRaw = (state: RootState) =>
  selectLoyaltyRedemption(state)?.offer;

export const selectRedeemedOfferApplicationId = (state: RootState) =>
  selectLoyaltyRedemption(state)?.offerApplicationId;

export const selectLoyaltyAddedItemLineIds = (state: RootState) =>
  selectLoyaltyRedemption(state)?.addedLineIds;

export const selectSelectedLoyaltyOfferId = (state: RootState) =>
  selectLoyaltyApplyFlow(state)?.selectedOfferId;

/** Sub-item uuids of the cart lines pushed by the active redemption. */
export const selectRedeemedLineSubItemIds = createSelector(
  selectLoyaltyCartLines,
  selectLoyaltyAddedItemLineIds,
  (lines, addedLineIds): string[] => {
    if (!addedLineIds?.length) {
      return [];
    }

    const addedIds = new Set(addedLineIds);

    return lines
      .filter((line) => line.lineId && addedIds.has(line.lineId))
      .map((line) => line.subItemId);
  }
);

/**
 * `includeEntityIds` for `OfferGraphicCollage` on applied-offer surfaces.
 * `undefined` when nothing is applied, which the collage reads as "show all" —
 * an empty array would instead blank it out.
 */
export const selectAppliedOfferGraphicEntityIds = createSelector(
  selectRedeemedLineSubItemIds,
  (subItemIds): string[] | undefined =>
    subItemIds.length ? subItemIds : undefined
);

export const selectLoyaltyOfferError = (state: RootState) =>
  selectLoyaltyCart(state).offerError;
